// VER-218: reusable step-up (re-OTP) primitive for sensitive Console actions
// (Step 11.1). Role change, preprod toggle, member removal, per-API perms, and
// allowed-domain edits all require a fresh X-Step-Up-Token; this hook owns the
// email-code → verify → token flow and the modal that drives it.
//
// Usage:
//   const { withStepUp, modal } = useStepUp();
//   ...
//   await withStepUp((token) => usersApi.changeRole(id, role, token));
//   return (<>{modal}{/* ...the screen */}</>);
//
// A single verification unlocks a ~5-min window (see stepUpStore), so a burst of
// changes prompts once. withStepUp auto-retries once if the server rejects a
// stale token (403 STEP_UP_REQUIRED).
import { useCallback, useRef, useState } from "react";
import { Modal, Typography, Button, message } from "antd";
import { authApi } from "./consoleApi";
import OtpInput from "./OtpInput";
import { getStepUpToken, setStepUpToken, clearStepUpToken } from "./stepUpStore";

export function useStepUp() {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState(null);
  const resolverRef = useRef(null); // pending { resolve, reject } for the open prompt

  // Open the modal and email a fresh code; returns a promise that settles when the
  // user verifies (token) or cancels (reject).
  const startPrompt = useCallback(async () => {
    setCode("");
    setError(null);
    setOpen(true);
    setSending(true);
    try {
      await authApi.stepUpInit();
    } catch {
      setError("Couldn't send the verification code. Use “Resend” to try again.");
    } finally {
      setSending(false);
    }
    return new Promise((resolve, reject) => {
      resolverRef.current = { resolve, reject };
    });
  }, []);

  // Resolve with a valid step-up token, prompting only if we don't already hold a
  // fresh one. Pass { force: true } to bypass the cache (e.g. after a stale-token
  // rejection).
  const requireStepUp = useCallback(
    async ({ force = false } = {}) => {
      if (force) clearStepUpToken();
      else {
        const cached = getStepUpToken();
        if (cached) return cached;
      }
      return startPrompt();
    },
    [startPrompt]
  );

  const submit = useCallback(
    async (value) => {
      const c = value ?? code;
      if (!c || c.length !== 6) return;
      setVerifying(true);
      setError(null);
      try {
        const res = await authApi.stepUp(c);
        const token = res.data.data.step_up_token;
        setStepUpToken(token);
        setOpen(false);
        resolverRef.current?.resolve(token);
        resolverRef.current = null;
      } catch {
        setError("That code didn't work. Check it and try again.");
      } finally {
        setVerifying(false);
      }
    },
    [code]
  );

  const cancel = useCallback(() => {
    setOpen(false);
    resolverRef.current?.reject(new Error("STEP_UP_CANCELLED"));
    resolverRef.current = null;
  }, []);

  const resend = useCallback(async () => {
    setSending(true);
    setError(null);
    try {
      await authApi.stepUpInit();
      message.success("A new code is on its way.");
    } catch {
      setError("Couldn't resend the code. Try again in a moment.");
    } finally {
      setSending(false);
    }
  }, []);

  // Run a step-up-gated action. Acquires a token (prompting if needed), then retries
  // once with a forced re-prompt if the server says the token is stale/missing.
  const withStepUp = useCallback(
    async (fn) => {
      let token = await requireStepUp();
      try {
        return await fn(token);
      } catch (e) {
        const errCode = e?.response?.data?.error?.code;
        if (e?.response?.status === 403 && errCode === "STEP_UP_REQUIRED") {
          token = await requireStepUp({ force: true });
          return fn(token);
        }
        throw e;
      }
    },
    [requireStepUp]
  );

  const modal = (
    <Modal
      open={open}
      title="Confirm it's you"
      onCancel={cancel}
      footer={null}
      destroyOnHidden
      maskClosable={false}
    >
      <Typography.Paragraph type="secondary">
        This is a sensitive change. We emailed a 6-digit code to your address — enter
        it to continue.
      </Typography.Paragraph>
      <OtpInput value={code} onChange={setCode} onComplete={submit} disabled={verifying} />
      {error && (
        <Typography.Paragraph type="danger" style={{ marginTop: 12, marginBottom: 0 }}>
          {error}
        </Typography.Paragraph>
      )}
      <div
        style={{
          marginTop: 16,
          display: "flex",
          gap: 8,
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Button type="link" onClick={resend} loading={sending} disabled={verifying}>
          Resend code
        </Button>
        <div style={{ display: "flex", gap: 8 }}>
          <Button onClick={cancel} disabled={verifying}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={() => submit()}
            loading={verifying}
            disabled={code.length !== 6}
          >
            Confirm
          </Button>
        </div>
      </div>
    </Modal>
  );

  return { requireStepUp, withStepUp, modal };
}
